import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError, timer } from 'rxjs';
import { Router } from '@angular/router';
import { TokenRefreshService, isSoftRefreshFailure } from './token-refresh.service';
import { HeaderConstant } from './header-constant';
import { API_TYPE, RETRY_ENDPOINTS, TELEMETRY_REQUEST } from './api-type';
import { ApiError } from './api-error';
import { logAuthSession } from '../auth/auth-session-log';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { NetworkConstant } from './network-constant';

const MAX_RETRIES = 3;

/** Marks a request as "already retried once for 401" so refresh-then-retry never loops. */
const RETRIED_CONTEXT = new HttpContextToken<boolean>(() => false);

/**
 * Angular functional-interceptor port of Consumer's
 * network/ResponseInterceptor.ts response handler. Same two behaviors:
 *
 * 1. 401 on an `AccessToken`-classified request -> attempt a (single-flight,
 *    see token-refresh.service.ts) token refresh, retry the original request
 *    once with the new bearer token; on an auth rejection of the refresh, force-logout via
 *    {@link TokenRefreshService.forceLogout} (session clear, multi-tab, login). A transient
 *    refresh failure (see `isSoftRefreshFailure`) keeps the session.
 * 2. 408 / 5xx on a small allowlist of endpoints -> retry with exponential
 *    backoff (2s, 4s, 5s-capped), up to 3 attempts, matching Consumer exactly.
 *
 * All terminal rejections are normalized into {@link ApiError}.
 */
export const responseInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenRefresh = inject(TokenRefreshService);
  const router = inject(Router);
  const connectivity = inject(ConnectivityService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      // status 0 == ApiError.isNetworkError — surface it app-wide via the
      // "Connection lost"/"No internet connection" overlay, in addition to
      // whatever this specific caller does with the rejected ApiError below.
      if (err.status === 0) connectivity.reportNetworkError();

      // ── Telemetry 401 — pause sync, never log out ──
      // A telemetry request uses the analytics API key and access token. A 401 means the token
      // has lapsed; the sync engine will resume on the next valid authenticated state.
      // Do NOT feed this into the refresh/logout logic — the active session must not be interrupted.
      if (err.status === 401 && req.context.get(TELEMETRY_REQUEST)) {
        return throwError(() => err);
      }

      const apiType = req.context.get(API_TYPE);

      // ── Token refresh on 401 (AccessToken requests only, and only once per request) ──
      // The refresh call itself goes through this interceptor and classifies as `AccessToken`, so a
      // 401 from it used to re-enter `tokenRefresh.refresh()` — which returns the very in-flight
      // promise this chain is inside, so the request never settled and the caller hung forever
      // (logout appeared to do nothing). A 401 here means the refresh token is dead: fall through
      // to the terminal handling below, which logs out and surfaces the error.
      const isRefreshCall = req.url.includes(NetworkConstant.REFRESH_ACCESS_TOKEN);

      if (
        err.status === 401 &&
        apiType === 'AccessToken' &&
        !isRefreshCall &&
        !req.context.get(RETRIED_CONTEXT)
      ) {
        logAuthSession('401-triggering-refresh', 'warn', { url: req.url });

        return from(tokenRefresh.refresh('401-retry')).pipe(
          // Only the *refresh* failing is handled here. The retried request's own failure (below)
          // is the request's answer — e.g. provisioning status replies HTTP 400 for `in_progress` —
          // and must never be mistaken for a refresh failure and log the user out.
          catchError((refreshErr: unknown) => {
            // Offline / timeout / 5xx / API key not ready says nothing about the refresh token:
            // keep the session and surface the failure to this caller; the next request or
            // resume re-attempts. Same classifier as the session gate — this path used to log
            // out on *any* refresh error.
            if (isSoftRefreshFailure(refreshErr)) {
              logAuthSession('401-refresh-soft-failure-kept', 'warn', { url: req.url });
              // The refresh POST itself passed through this interceptor, so an HTTP failure
              // arrives as ApiError; anything else (timeout, cancelled) reports the original 401.
              return throwError(() =>
                refreshErr instanceof ApiError ? refreshErr : ApiError.fromHttpErrorResponse(err),
              );
            }
            logAuthSession('401-refresh-failed-logout', 'warn', { url: req.url });
            return from(tokenRefresh.forceLogout(router.url)).pipe(
              switchMap(() => throwError(() => ApiError.fromHttpErrorResponse(err))),
            );
          }),
          switchMap((newJwt) => {
            const retried = req.clone({
              setHeaders: { [HeaderConstant.HEADER_AUTHORIZATION]: newJwt },
              context: req.context.set(RETRIED_CONTEXT, true),
            });
            return next(retried).pipe(
              catchError((retryErr: unknown) => {
                if (!(retryErr instanceof HttpErrorResponse)) return throwError(() => retryErr);
                if (retryErr.status === 0) connectivity.reportNetworkError();
                return throwError(() => ApiError.fromHttpErrorResponse(retryErr));
              }),
            );
          }),
        );
      }

      // ── Retry 408 / 5xx on selected endpoints, exponential backoff ──
      const shouldConsiderRetry =
        (err.status === 408 || (err.status >= 500 && err.status < 600)) &&
        RETRY_ENDPOINTS.some((endpoint) => req.url.includes(endpoint));

      if (shouldConsiderRetry) {
        return attemptRetry(req, next, err, 0, connectivity);
      }

      return throwError(() => ApiError.fromHttpErrorResponse(err));
    }),
  );
};

function attemptRetry(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: HttpErrorResponse,
  retryCount: number,
  connectivity: ConnectivityService,
): Observable<HttpEvent<unknown>> {
  if (retryCount >= MAX_RETRIES) {
    return throwError(() => ApiError.fromHttpErrorResponse(originalError));
  }

  const backoffMs = Math.min(2000 * Math.pow(2, retryCount), 5000);

  return timer(backoffMs).pipe(
    switchMap(() =>
      next(req).pipe(
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 0) connectivity.reportNetworkError();
          if (err instanceof HttpErrorResponse && (err.status === 408 || (err.status >= 500 && err.status < 600))) {
            return attemptRetry(req, next, err, retryCount + 1, connectivity);
          }
          return throwError(() => (err instanceof HttpErrorResponse ? ApiError.fromHttpErrorResponse(err) : err));
        }),
      ),
    ),
  );
}
